import styled, { useTheme } from 'styled-components';
import { Box } from 'reflexbox';
import { Line } from 'react-chartjs-2';

import 'chart.js/auto';

const Container = styled(Box)<{ height?: number }>`
	position: relative;
	height: ${(props) => props.height}px;
`;

const Graph = styled(Box)`
	position: absolute;
	width: 100%;
	height: 100%;
`;

export default function LineMetric({
	labels,
	values,
	color,
	height,
	grid,
	padding,
	xTicks,
	yTicks,
	yMin,
	yMax,
	yStepSize,
	tooltip,
	yFormat,
}: {
	labels: string[];
	values: ({ x: number; y: number; tooltip?: string } | number | undefined)[];
	color?: string;
	height?: number;
	grid?: boolean;
	padding?: number;
	xTicks?: boolean;
	yTicks?: boolean;
	yMin?: number;
	yMax?: number;
	yStepSize?: number;
	tooltip?: boolean;
	yFormat?: (x: number) => string;
}) {
	const theme = useTheme() as any;

	const options = {
		responsive: true,
		layout: {
			padding: padding ?? 20,
		},
		scales: {
			y: {
				beginAtZero: true,
				suggestedMin: yMin,
				suggestedMax: yMax,
				grid: {
					display: grid ?? true,
					color: theme.colors.whiteBlue,
					drawBorder: false,
				},
				ticks: {
					display: yTicks ?? true,
					precision: 0,
					color: theme.colors.slateBlue,
					stepSize: yStepSize,
					autoSkip: typeof yStepSize !== 'number',
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
					callback: yFormat,
				},
			},
			x: {
				grid: {
					display: false,
					drawBorder: false,
				},
				ticks: {
					display: xTicks ?? true,
					color: theme.colors.slateBlue,
					font: {
						weight: 'bold',
						size: 12,
						family: 'Source Sans Pro',
					},
				},
			},
		},
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				enabled: tooltip ?? true,
				callbacks: {
					label: function (context: any) {
						const source = context.dataset.data[context.dataIndex];

						if (source?.tooltip) {
							return source?.tooltip;
						}

						return yFormat ? yFormat(context.parsed.y) : context.parsed.y.toFixed(1);
					},
				},
			},
		},
		maintainAspectRatio: false,
	};

	return (
		<Box pt={(padding ?? 20) > 0 ? 3 : 0}>
			<Container height={height ?? 165}>
				<Graph>
					<Line
						data={{
							labels,
							datasets: [
								{
									data: values,
									fill: false,
									backgroundColor: color ? theme.colors[color] : theme.colors.primary,
									borderColor: color ? theme.colors[color] : theme.colors.primary,
									tension: 0.4,
								},
							],
						}}
						options={options}
						height={height ?? 165}
					/>
				</Graph>
			</Container>
		</Box>
	);
}
